import chalk from "chalk";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import type { Translations } from "@/features/translations/types";
import { getRequestBuilder } from "@/lib/fetcher.ts";
import Logger from "@/lib/logger.ts";
import type { DownloadOption, Locale, Nullable, ResponsiveImage, SiteEnum } from "@/types";
import type {
    DynamicSpecSheet,
    ModelUrl,
    Part,
    ProductData,
    SmartNumber,
    SmartNumberDescription,
    SmartNumberMapping,
    SubFamily,
    SubFamilyId,
    ZipDownloadPath,
} from "../types";

export default async function getSubFamilyConfig(
    modelCode: Nullable<string>,
    smartNumber: Nullable<SmartNumber>,
    site: SiteEnum,
    locale: Locale,
) {
    if (!modelCode && !smartNumber) throw new Error("Need at least a modelCode or smartNumber!");

    Logger.info("Fetching configuration for smart number", chalk.cyan(smartNumber));
    DiagnosticsStore.collectEventLog("info", `Fetching configuration for smartnumber ${smartNumber}`);

    const response = await fetchConfigurator(smartNumber, modelCode, site, locale);

    if (!response.success) {
        DiagnosticsStore.collectEventLog("error", `Failed fetching configuration for smartnumber ${smartNumber}`);

        const message = Object.entries(response.errors)
            .reduce<string[]>((acc, curr) => {
                acc.push(`${curr[0]}: ${curr[1]}`);

                return acc;
            }, [])
            .join("\n");

        throw new Error(message);
    }

    return response.view;
}

function fetchConfigurator(smartNumber: Nullable<string>, modelCode: Nullable<string>, site: SiteEnum, locale: Locale) {
    const requester = getRequestBuilder(site, locale);

    return requester<ApiSuccess, ApiError>("/catalog/product-configurator/", {
        smartnumber: smartNumber ?? undefined, // Always add smart number when it is present
        modelcode: !smartNumber ? (modelCode ?? undefined) : undefined, // Only add modelCode when smart number is not set
    });
}

type ApiSuccess = {
    view: {
        allParts: Nullable<ResponsiveImage>;
        allowAllParts: boolean;
        downloadOptions: DownloadOption[];
        dynamicSpecsheetPath: DynamicSpecSheet;
        family: string;
        glbUrl: ModelUrl;
        id: SubFamilyId;
        labels: Translations;
        parts: Part[];
        productData: ProductData;
        smartNumberMapping: SmartNumberMapping;
        smartnumberDescription: SmartNumberDescription;
        subFamilies: SubFamily[];
        title: string;
        crmFormPath: string;
        zipDownloadPath: ZipDownloadPath;
        whistlistAddPath: string;
        whistlistRemovePath: string;
        wishlistListPath: string;
        pinnedCollectionAddPath: string;
        pinnedCollectionRemovePath: string;
    };
};
type ApiError = { errors: Record<string, string> };
